// LabScreen (§5) — the Combination Lab: a 4-slot workbench that reacts any valid
// pair among the staged elements, consuming the two reactants and leaving the
// product on the bench. Shares the discovery celebration + combo praise.
import { AD_SLOTS } from "../../config/adSlots";
import React, { useMemo, useRef, useState } from "react";
import Screen from "../ui/Screen";
import ElementOrb from "../ui/ElementOrb";
import DiscoveryOverlay from "./DiscoveryOverlay";
import AchievementToast from "../ui/AchievementToast";
import { useElementStore } from "../../store/elementStore";
import { ELEMENT_DEFS } from "../elementDefinitions";
import { playSound } from "../../services/soundService";
import { triggerHaptic } from "../../services/capacitorService";
import { addXP, checkAchievements, Achievement } from "../../services/progressionService";
import { levelFromDiscovered, getStreak, touchStreak } from "../../game/stats";
import { makePraise, Praise } from "../../game/praise";
import { share } from "../../game/share";
import type { Feedback } from "../../game/useForge";

type Bench = [string | null, string | null, string | null, string | null];

const LabScreen: React.FC = () => {
  const all = useElementStore((s) => s.all);
  const unlocked = useElementStore((s) => s.unlocked);
  const lookupRecipe = useElementStore((s) => s.lookupRecipe);
  const discoverRecipe = useElementStore((s) => s.discoverRecipe);

  const [bench, setBench] = useState<Bench>([null, null, null, null]);
  const [query, setQuery] = useState("");
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [discovery, setDiscovery] = useState<{ id: string; ingredients: string[] } | null>(null);
  const [praise, setPraise] = useState<Praise | null>(null);
  const [achievement, setAchievement] = useState<Achievement | null>(null);

  const comboRef = useRef(0);
  const lastDiscRef = useRef(0);
  const praiseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const discovered = unlocked.size;
  const level = levelFromDiscovered(discovered);
  const streak = getStreak().streak;

  const tray = useMemo(() => {
    const ids = Array.from(unlocked).filter((id) => all[id]);
    const q = query.trim().toLowerCase();
    const filtered = q ? ids.filter((id) => all[id].name.toLowerCase().includes(q)) : ids;
    return filtered.sort((a, b) => (all[a].tier - all[b].tier) || all[a].name.localeCompare(all[b].name));
  }, [unlocked, all, query]);

  const pickTray = (id: string) => {
    triggerHaptic("light");
    playSound("select");
    setFeedback(null);
    setBench((s) => {
      const next = [...s] as Bench;
      const idx = next.indexOf(null);
      if (idx !== -1) next[idx] = id;
      return next;
    });
  };

  const clearBenchSlot = (i: number) => {
    setBench((s) => {
      const next = [...s] as Bench;
      next[i] = null;
      return next;
    });
  };
  const clearBench = () => {
    setBench([null, null, null, null]);
    setFeedback(null);
  };

  const combine = () => {
    triggerHaptic("medium");
    playSound("combine");
    const filled = bench.filter(Boolean) as string[];
    if (filled.length < 2) {
      setFeedback({ text: "Stage at least two elements on the bench.", kind: "info" });
      return;
    }
    for (let i = 0; i < filled.length; i++) {
      for (let j = i + 1; j < filled.length; j++) {
        const res = lookupRecipe([filled[i], filled[j]]);
        if (!res) continue;
        const known = unlocked.has(res);
        const rest = filled.filter((_, k) => k !== i && k !== j);
        const next = [res, ...rest].slice(0, 4);
        const newBench = [...next, null, null, null, null].slice(0, 4) as Bench;

        if (!known) {
          discoverRecipe([filled[i], filled[j]]); // unlock
          triggerHaptic("success");
          playSound("discover");
          touchStreak();
          addXP(all[res].tier * 10);
          const now = Date.now();
          const combo = now - lastDiscRef.current < 5000 ? comboRef.current + 1 : 1;
          comboRef.current = combo;
          lastDiscRef.current = now;
          const p = makePraise(combo, all[res].tier, now);
          setPraise(p);
          if (praiseTimer.current) clearTimeout(praiseTimer.current);
          praiseTimer.current = setTimeout(() => setPraise(null), 1500);
          setBench(newBench);
          setDiscovery({ id: res, ingredients: [filled[i], filled[j]] });
          const earned = checkAchievements(useElementStore.getState().unlocked, 0);
          if (earned.length) setTimeout(() => setAchievement(earned[0]), 1800);
        } else {
          setBench(newBench);
          setFeedback({ text: `${all[res].name} formed (already known).`, kind: "info" });
        }
        return;
      }
    }
    triggerHaptic("error");
    playSound("error");
    setFeedback({ text: "No reaction between any of these. Swap one out.", kind: "warn" });
  };

  const onShare = async () => {
    if (!discovery) return;
    await share(`I just discovered ${ELEMENT_DEFS[discovery.id].name} in MystiCrafter! ⚗️`);
  };

  const feedbackStyle = (kind: Feedback["kind"]): React.CSSProperties => {
    const map = {
      info: { bg: "rgba(96,165,250,0.12)", color: "#93C5FD", border: "rgba(96,165,250,0.3)" },
      warn: { bg: "rgba(245,183,64,0.12)", color: "#F5B740", border: "rgba(245,183,64,0.3)" },
      good: { bg: "rgba(52,211,153,0.12)", color: "#34D399", border: "rgba(52,211,153,0.3)" },
    }[kind];
    return { margin: "14px auto 0", maxWidth: 360, padding: "10px 14px", borderRadius: 12, textAlign: "center", fontSize: 13, fontWeight: 600, background: map.bg, color: map.color, border: `1px solid ${map.border}` };
  };

  const overlay = (
    <>
      <DiscoveryOverlay elementId={discovery?.id ?? null} ingredients={discovery?.ingredients} praise={praise} onShare={onShare} onClose={() => setDiscovery(null)} />
      <AchievementToast achievement={achievement} onClose={() => setAchievement(null)} />
    </>
  );

  return (
    <Screen title="Combination Lab" active="forge" showBack streak={streak} level={level} overlay={overlay} ad={AD_SLOTS.gameplay}>
      {/* Bench */}
      <div style={{ padding: "20px 16px 18px", background: "radial-gradient(120% 90% at 50% 0%, var(--hero-1) 0%, var(--hero-2) 55%, var(--bg) 100%)", borderBottom: "1px solid var(--border)" }}>
        <div style={{ textAlign: "center", fontSize: 12, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--accent-text)" }}>
          Stage up to 4 · any reacting pair transforms
        </div>
        <div style={{ marginTop: 16, display: "flex", justifyContent: "center", gap: 10, flexWrap: "wrap" }}>
          {bench.map((id, i) => (
            <button
              key={i}
              className="mc-tap"
              onClick={() => id && clearBenchSlot(i)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); const d = e.dataTransfer.getData("text/plain"); if (d) setBench((s) => { const n = [...s] as Bench; if (n[i] === null) n[i] = d; return n; }); }}
              style={{ width: 68, height: 68, borderRadius: 18, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", background: id ? "rgba(101,163,13,0.1)" : "var(--fill)", border: id ? "2px solid rgba(101,163,13,0.55)" : "2px dashed var(--track)" }}
              aria-label={id ? `Remove ${ELEMENT_DEFS[id]?.name}` : `Empty bench slot ${i + 1}`}
            >
              {id ? <ElementOrb id={id} size={44} /> : <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#5B5687" strokeWidth={2} strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>}
            </button>
          ))}
        </div>
        <div style={{ marginTop: 16, display: "flex", gap: 10, justifyContent: "center" }}>
          <button className="mc-tap" onClick={combine} style={{ minHeight: 48, padding: "0 28px", border: "none", borderRadius: 14, fontSize: 15, fontWeight: 700, cursor: "pointer", background: "#65A30D", color: "#fff", boxShadow: "0 8px 20px rgba(101,163,13,0.35)" }}>React</button>
          <button className="mc-tap" onClick={clearBench} aria-label="Clear bench" style={{ minHeight: 48, width: 48, display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 14, background: "var(--fill)", color: "var(--text-2)", cursor: "pointer" }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" /></svg>
          </button>
        </div>
        {feedback && <div style={feedbackStyle(feedback.kind)}>{feedback.text}</div>}
      </div>

      {/* Tray */}
      <div style={{ padding: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0, height: 40, padding: "0 12px", borderRadius: 12, background: "var(--surface)", border: "1px solid var(--border)" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-3)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4" /></svg>
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search your elements…" aria-label="Search your elements" style={{ flex: 1, background: "transparent", border: "none", outline: "none", color: "var(--text)", fontSize: 13, minWidth: 0 }} />
          </div>
          <span style={{ fontSize: 12, color: "var(--text-3)", whiteSpace: "nowrap" }}>{tray.length} elements</span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(74px,1fr))", gap: 12 }}>
          {tray.map((id) => (
            <button key={id} className="mc-tap" draggable onDragStart={(e) => e.dataTransfer.setData("text/plain", id)} onClick={() => pickTray(id)} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 7, padding: "10px 4px", borderRadius: 16, background: "var(--surface)", border: "1px solid var(--border)", cursor: "pointer" }}>
              <ElementOrb id={id} size={44} />
              <span style={{ fontSize: 11, color: "var(--text-2)", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}>{ELEMENT_DEFS[id]?.name}</span>
            </button>
          ))}
        </div>
      </div>
    </Screen>
  );
};

export default LabScreen;
